import React, { FC, useEffect, useState } from 'react';

import { motion } from 'framer-motion';

import styled from 'styled-components';

// Constants
import aampeTheme from '../../../../constants/aampeTheme';

// Models
import {
    MobilePushPreviewModel,
    SMSPreviewModel,
} from '../../../../models/editor.model';

// Components
import CoverMessagePreview from './CoverMessagePreview';

interface AnimatedMessageLoaderProps {
    fetchedMessages: (MobilePushPreviewModel | null)[];
}
const numberOfSkeletonMessages = 40;
const gridVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.8, // Stagger animation to create wave
        },
    },
};
const waveMessageVariants = {
    hidden: { opacity: 0, y: 50 }, // Start slightly below
    visible: {
        opacity: 1,
        y: [0, -7, 0], // Wave-like motion: go up slightly, then return
        transition: {
            duration: 0.9, // Speed of each wave
            ease: 'easeInOut', // Smooth wave transition
        },
    },
};
const skeletonVariants = {
    pulse: (delay: number) => ({
        opacity: [1, 0.5, 1],
        scale: [1, 1.01, 1],
        transition: {
            duration: 1.5,
            delay: delay,
            repeat: 3,
            ease: 'easeInOut',
        },
    }),
};
const AnimatedMessageLoader: FC<
    React.PropsWithChildren<AnimatedMessageLoaderProps>
> = ({ fetchedMessages }) => {
    const [messages, setMessages] = useState<
        (MobilePushPreviewModel | SMSPreviewModel | null)[]
    >(Array(numberOfSkeletonMessages).fill(null));

    useEffect(() => {
        const loadMessages = async () => {
            // Replace each skeleton with the actual message data individually
            fetchedMessages.forEach(
                (
                    msg: MobilePushPreviewModel | SMSPreviewModel | null,
                    index: number,
                ) => {
                    setTimeout(() => {
                        setMessages((prev) => {
                            const newMessages = [...prev];
                            newMessages[index] = msg;
                            return newMessages;
                        });
                    }, index * 100);
                },
            );
        };

        loadMessages();
    }, [fetchedMessages]);

    return (
        <StyledGrid
            className="columns"
            initial="hidden"
            animate="visible"
            variants={gridVariants}
        >
            {messages.map((message, index) => (
                <motion.div
                    key={message?.id ? message.id + index : `skeleton-${index}`}
                    className={message ? 'loaded-message' : 'skeleton-message'}
                    variants={message ? waveMessageVariants : skeletonVariants}
                    initial={message ? 'hidden' : undefined}
                    animate={message ? 'visible' : ['pulse']}
                    custom={!message ? Math.random() * 3 : undefined}
                >
                    <CoverMessagePreview
                        message={message}
                        isSkeleton={!message}
                    />
                </motion.div>
            ))}
        </StyledGrid>
    );
};

export default AnimatedMessageLoader;

const StyledGrid = styled(motion.div)({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(241px, 1fr))',
    gap: aampeTheme.spacing4(6, 6),
    position: 'relative',
    zIndex: 10,
    overflowY: 'auto',
    overflowX: 'auto',
    maxHeight: `calc(100vh - 428px)`,

    '@media (min-width: 768px)': {
        gap: aampeTheme.spacing4(6),
        padding: aampeTheme.spacing4(8),
    },
    '@media (min-width: 1200px)': {
        gap: aampeTheme.spacing4(9, 7),
        padding: aampeTheme.spacing4(10),
    },
    '@media (min-width: 1500px)': {
        gap: aampeTheme.spacing4(9, 11),
    },
    '@media (min-width: 1700px)': {
        gap: aampeTheme.spacing4(9, 11),
    },
    '@media (min-width: 1900px)': {
        gap: aampeTheme.spacing4(9, 12),
        padding: aampeTheme.spacing4(10),
    },
    '@media (min-width: 2100px)': {
        rowGap: aampeTheme.spacing4(10),
        columnGap: aampeTheme.spacing4(14),
    },
});
